
import { useState, useEffect, useRef, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AuthPageLayout from '@/components/auth/AuthPageLayout';

const MOCK_CODE = '123456';

function computeRemainingSeconds(otpExpiresAt: string): number {
  return Math.floor((new Date(otpExpiresAt).getTime() - Date.now()) / 1000);
}

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, isLoading, otpExpiresAt, verifyOtp, resendOtp, logout } = useAuth();
  const isMock = import.meta.env.VITE_ENABLE_MOCK === 'true';

  const [digits, setDigits] = useState<string[]>(
    isMock ? MOCK_CODE.split('') : Array(6).fill('')
  );
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, isLoading, navigate]);

  // Sincronizar countdown con otpExpiresAt (localStorage o contexto)
  useEffect(() => {
    if (isMock) return;
    const value = otpExpiresAt ?? localStorage.getItem('flux_otp_expires_at');
    if (!value) {
      setCountdown(0);
      setCanResend(true);
      return;
    }
    const remaining = computeRemainingSeconds(value);
    setCountdown(remaining);
    setCanResend(remaining <= 0);
  }, [otpExpiresAt, isMock]);

  // Tick cada segundo, habilitar resend al llegar a 0
  useEffect(() => {
    if (countdown === null || countdown <= 0) {
      if (countdown !== null) setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => (c ?? 1) - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      const char = value.replace(/\D/g, '').slice(-1);
      const next = [...digits];
      next[index] = char;
      setDigits(next);
      setError('');
      if (char && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [digits]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      if (!pasted) return;
      const next = [...digits];
      for (let i = 0; i < pasted.length; i++) {
        next[i] = pasted[i];
      }
      setDigits(next);
      const focusIndex = Math.min(pasted.length, 5);
      inputRefs.current[focusIndex]?.focus();
    },
    [digits]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 6) {
      setError('Ingresa los 6 dígitos del código');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await verifyOtp(code);
      navigate('/verify-email/confirm');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Código incorrecto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCanResend(false);
    setDigits(isMock ? MOCK_CODE.split('') : Array(6).fill(''));
    setError('');
    try {
      await resendOtp();
      // El useEffect recalcula el countdown cuando otpExpiresAt cambia
    } catch (err: unknown) {
      setCanResend(true);
      setError(err instanceof Error ? err.message : 'Error al reenviar el código');
    }
  };

  const formatCountdown = (s: number) => {
    const abs = Math.abs(s);
    const sign = s < 0 ? '-' : '';
    const mm = String(Math.floor(abs / 60)).padStart(2, '0');
    const ss = String(abs % 60).padStart(2, '0');
    return `${sign}${mm}:${ss}`;
  };

  return (
    <AuthPageLayout
      leftIcon="security"
      leftTitle={
        <>
          Seguridad en<br />cada paso
        </>
      }
      leftSubtitle="Protegemos tu cuenta y tus transacciones con los más altos estándares de seguridad bancaria."
    >
      <div className="absolute top-6 left-6 z-20">
        <button
          type="button"
          onClick={() => logout()}
          className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-red-500 transition-colors font-medium"
        >
          <span className="material-icons text-sm">logout</span>
          Cerrar sesión
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-12 lg:p-24">
        <div className="w-full max-w-md space-y-8">
          <div className="flex md:hidden justify-center mb-6">
            <div className="flex items-center gap-2">
              <span className="material-icons text-primary text-3xl">account_balance_wallet</span>
              <span className="font-bold text-2xl tracking-tight text-gray-900 dark:text-white">FLUX</span>
            </div>
          </div>

          <div className="text-left">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center md:hidden">
                <span className="material-icons text-primary text-xl">mark_email_read</span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Verifica tu correo
              </h2>
            </div>
            <p className="text-slate-500 dark:text-gray-400 mt-2">
              Hemos enviado un código a{' '}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {user?.email ?? '...'}
              </span>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
              <span className="material-icons text-base">error</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Código de verificación
              </label>
              <div className="flex gap-2 sm:gap-4 justify-between">
                {digits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    className="w-full aspect-square text-center text-2xl font-bold border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-surface-dark dark:text-white shadow-sm transition-colors"
                  />
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Verificando...' : 'Verificar código'}
              <span className="absolute right-4 flex items-center">
                <span className="material-icons text-blue-200 group-hover:text-white transition-colors text-sm">
                  arrow_forward
                </span>
              </span>
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500 dark:text-gray-400 mb-2">
              ¿No recibiste el código?
            </p>
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={!canResend || countdown === null}
                className="font-medium text-primary hover:text-blue-600 transition-colors inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-lg">refresh</span>
                Reenviar código
              </button>
              {countdown !== null && countdown > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-500">
                  El código expira en {formatCountdown(countdown)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
}
